import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { useStores } from '~/hooks/useStore';
import { catalogStore, userStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore';

import { CarouselIcon } from '../../CommonCarousel';
import Icon from '../../Icon/Icon';

import styles from './HeaderMenu.module.scss';

type Props = {
  hideAllCategories?: boolean;
};

const HeaderMenu = observer(({ hideAllCategories }: Props) => {
  const { t } = useTranslation();
  const { handleClickCategory } = useCategoryAnalytics();
  const { shopCategories } = useStores();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const startShoppingSubcategories = shopCategories.list.reduce((acc, item) => {
    return [...acc, ...(item ? item.subcategory : [])];
  }, [] as Category[]);

  const carouselOpts = useMemo(
    () => ({
      align: 'start' as const,
      slidesToScroll: 3,
      containScroll: 'trimSnaps' as const,
      active: !prevBtnDisabled || !nextBtnDisabled,
      dragFree: true,
    }),
    [prevBtnDisabled, nextBtnDisabled],
  );

  return (
    <>
      <div
        className={classes(['content-layout', styles.container])}
        data-company={company.name}
      >
        <div className={styles.header_menu} data-company={company.name}>
          {!hideAllCategories && (
            <div className={styles.with_line}>
              <Link
                data-link="header-menu-all"
                to="/shop"
                className={styles.header_menu__item}
              >
                <Icon type="shop" size={20} />
                {t('allCategories')}
              </Link>
              <div className={styles.header_menu__line} />
            </div>
          )}

          {!prevBtnDisabled && (
            <CarouselIcon
              attributeName="header-menu-left-arrow"
              position="left"
              variant="white"
              className={classes([
                styles.icon_swipe,
                styles.button_circle_arrow,
              ])}
              iconClassName={classes([styles.icon])}
              onClick={onPrevButtonClick}
            />
          )}
          <div className={classes([styles.carouselWrapper])}>
            <Carousel
              setApi={setApi}
              opts={carouselOpts}
              className={styles.carousel}
              dir={userStore.dir}
              key={userStore.dir}
            >
              <CarouselContent
                style={{
                  gap: '20px',
                }}
                className={styles.carouselContent}
              >
                {startShoppingSubcategories.map(({ id, name }) => (
                  <CarouselItem className={styles.slide} key={id}>
                    <Link
                      data-link={`header-menu-category-${id}`}
                      key={id}
                      to={`/c/${id}`}
                      className={classes([styles.header_menu__item])}
                      onClick={() => {
                        handleClickCategory(id, 'header')();
                        catalogStore.closeCategoryShutter();
                      }}
                    >
                      {name}
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
          {!nextBtnDisabled && (
            <CarouselIcon
              attributeName="header-menu-right-arrow"
              position="right"
              variant="white"
              className={classes([
                styles.icon_swipe,
                styles.button_circle_arrow,
              ])}
              iconClassName={classes([styles.icon])}
              onClick={onNextButtonClick}
            />
          )}
        </div>
      </div>
    </>
  );
});

export default HeaderMenu;
